/** @app — application layer; owned per-fork (LMS / OTT). See PLATFORM_FILES.md. */
import type { LearningInstructorRef } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';

interface InstructorCardProps {
  instructor: LearningInstructorRef;
  size?: 'sm' | 'md' | 'lg';
  /** Optional bio paragraph — only rendered in `lg` size. The schema
   *  doesn't carry a bio field on the instructor ref, so this is
   *  passed in by the variant orchestrator when it has bio content
   *  from another source (e.g. enriched data). Stays optional. */
  bio?: string;
}

export function InstructorCard({ instructor, size = 'sm', bio }: InstructorCardProps) {
  const initials = getInitials(instructor.name ?? '?');
  const inner = (
    <div className={`cd-instructor cd-instructor-${size}`}>
      <div className="cd-instructor-avatar" aria-hidden>
        {instructor.avatar ? (
          <img
            src={instructor.avatar}
            alt=""
            loading="lazy"
            decoding="async"
            width={40}
            height={40}
          />
        ) : (
          <span>{initials}</span>
        )}
      </div>
      <div>
        <div className="cd-instructor-name">{instructor.name}</div>
        {instructor.role && <div className="cd-instructor-meta">{instructor.role}</div>}
        {size === 'lg' && bio && <p className="cd-instructor-bio">{bio}</p>}
      </div>
    </div>
  );
  // Only the `lg` size renders a bordered card surface; sm/md are bare rows.
  return size === 'lg' ? <Card asChild>{inner}</Card> : inner;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}
